import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {
    Row, Col, Card, Button, Form, Tab, Nav,
    Alert, Spinner, Badge, Modal, Table
} from 'react-bootstrap'
import * as FaIcons from 'react-icons/fa'
import { useSelector } from 'react-redux'

import SimpleMantenedorLayout from '../../../components/SimpleMantenedorLayout'
import { useMantenedores } from '../../../context/MantenedoresContext'
import {
    getEmailTemplateContent,
    createEmailTemplate,
    updateEmailTemplate,
    updateEmailTemplateContent,
    testEmailTemplate
} from '@helpers/api/mantenedores/emailTemplates'
import { SuccessSwal, ErrorSwal } from '@helpers/swal'

// ─── Quill toolbar config ──────────────────────────────────────────────────
const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean']
    ]
}

const QUILL_FORMATS = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'align', 'blockquote',
    'list', 'bullet', 'link'
]

// ─── HTML helpers ──────────────────────────────────────────────────────────
const extractBodyContent = (fullHtml) => {
    if (!fullHtml) return ''
    const bodyMatch = fullHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
    return bodyMatch ? bodyMatch[1].trim() : fullHtml
}

const wrapBodyInHtml = (bodyContent) => `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
    h1, h2, h3 { color: #444; }
    a { color: #1976d2; }
    blockquote { border-left: 3px solid #ccc; padding-left: 12px; color: #666; margin: 12px 0; }
    ul, ol { padding-left: 20px; }
  </style>
</head>
<body>
${bodyContent}
</body>
</html>`

// ─── Parsers ────────────────────────────────────────────────────────────────
const parseCamposConfig = (json, valoresEjemplo = {}) => {
    try {
        const parsed = typeof json === 'string' ? JSON.parse(json) : (json || [])
        if (!Array.isArray(parsed)) return []
        return parsed.map(c => ({
            campo: c.campo || '',
            label: c.label || '',
            tipo: c.tipo || 'texto',
            requerido: c.requerido !== false,
            activo: c.activo !== false,
            ayuda: c.ayuda || '',
            ejemplo: c.ejemplo || valoresEjemplo[c.campo] || ''
        }))
    } catch { return [] }
}

const parseCamposAuto = (json, valoresEjemplo = {}) => {
    try {
        const parsed = typeof json === 'string' ? JSON.parse(json) : (json || [])
        if (!Array.isArray(parsed)) return []
        return parsed.map(c =>
            typeof c === 'string'
                ? { campo: c, descripcion: '', ejemplo: valoresEjemplo[c] || '' }
                : { campo: c.campo || '', descripcion: c.descripcion || '', ejemplo: c.ejemplo || valoresEjemplo[c.campo] || '' }
        )
    } catch { return [] }
}

// ─── Tipo options ────────────────────────────────────────────────────────────
const TIPO_OPTIONS = [
    { value: 'texto', label: 'Texto' },
    { value: 'fecha', label: 'Fecha' },
    { value: 'fecha_lista', label: 'Lista de fechas' },
    { value: 'numero', label: 'Número' },
    { value: 'checkbox', label: 'Sí / No' },
    { value: 'select', label: 'Selección' }
]

const TIPO_LABELS = Object.fromEntries(TIPO_OPTIONS.map(t => [t.value, t.label]))

// ─── Default values ─────────────────────────────────────────────────────────
const DEFAULT_CAMPOS_CONFIG = [
    { campo: 'fecha_aceptacion_factura', label: 'Fecha límite aceptación facturas', tipo: 'fecha', requerido: true, activo: true, ayuda: '', ejemplo: '15 de Mayo' },
    { campo: 'fecha_pago', label: 'Fecha de pago', tipo: 'fecha', requerido: true, activo: true, ayuda: '', ejemplo: '20 de Mayo' },
    { campo: 'fecha_no_pago', label: 'Fechas de no pago', tipo: 'fecha_lista', requerido: true, activo: true, ayuda: '', ejemplo: 'Lunes 01 de abril y Viernes 18 de abril' },
    { campo: 'mensaje_tardio', label: 'Envío tardío', tipo: 'checkbox', requerido: false, activo: true, ayuda: 'Incluir mensaje de envío tardío', ejemplo: '' }
]

const DEFAULT_CAMPOS_AUTO = [
    { campo: 'fecha_inicio_cierre', descripcion: 'Fecha de inicio del periodo de cierre (ej: 01 de Abril)', ejemplo: '01 de Abril' },
    { campo: 'fecha_termino_cierre', descripcion: 'Fecha de término del periodo de cierre (ej: 30 de Abril)', ejemplo: '30 de Abril' },
    { campo: 'anio_cierre', descripcion: 'Año del cierre', ejemplo: '2026' }
]

const DEFAULT_QUILL_CONTENT = `<h2>Estimado {conductor_nombre},</h2>
<p>Adjunto encontrará el resumen de su cierre <strong>{cierre}</strong> correspondiente al periodo <strong>{periodo}</strong>.</p>
<p>Fecha de pago: <strong>{fecha_pago}</strong><br>Fecha límite aceptación facturas: <strong>{fecha_aceptacion_factura}</strong></p>
<p>Saludos cordiales,<br>Equipo Spread</p>`

// ─── CamposConfigTable ──────────────────────────────────────────────────────
const EMPTY_CAMPO = { campo: '', label: '', tipo: 'texto', requerido: true, activo: true, ayuda: '', ejemplo: '' }

const CamposConfigTable = ({ campos, onChange }) => {
    const [showAdd, setShowAdd] = useState(false)
    const [draft, setDraft] = useState(EMPTY_CAMPO)

    const toggle = (idx, key) =>
        onChange(campos.map((c, i) => i === idx ? { ...c, [key]: !c[key] } : c))

    const remove = (idx) => onChange(campos.filter((_, i) => i !== idx))

    const add = () => {
        if (!draft.campo.trim() || !draft.label.trim()) return
        const sanitized = draft.campo.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_')
        onChange([...campos, { ...draft, campo: sanitized }])
        setDraft(EMPTY_CAMPO)
        setShowAdd(false)
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <strong>Variables configurables por el usuario</strong>
                    <div>
                        <small className="text-muted">
                            Campos que el usuario completa en el modal de envío. El <em>valor ejemplo</em> se usa en el correo de prueba.
                        </small>
                    </div>
                </div>
                <Button variant="outline-primary" size="sm" onClick={() => setShowAdd(v => !v)}>
                    <FaIcons.FaPlus className="me-1" />Agregar variable
                </Button>
            </div>

            {showAdd && (
                <Card className="mb-3 border-primary">
                    <Card.Body className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col xs={12} sm={3}>
                                <Form.Label className="small mb-1">Nombre variable *</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={draft.campo}
                                    onChange={e => setDraft(p => ({
                                        ...p,
                                        campo: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                                    }))}
                                    placeholder="ej: fecha_pago"
                                />
                            </Col>
                            <Col xs={12} sm={3}>
                                <Form.Label className="small mb-1">Descripción / Etiqueta *</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={draft.label}
                                    onChange={e => setDraft(p => ({ ...p, label: e.target.value }))}
                                    placeholder="ej: Fecha de pago"
                                />
                            </Col>
                            <Col xs={12} sm={2}>
                                <Form.Label className="small mb-1">Valor ejemplo</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={draft.ejemplo}
                                    onChange={e => setDraft(p => ({ ...p, ejemplo: e.target.value }))}
                                    placeholder="ej: 20 de Mayo"
                                />
                            </Col>
                            <Col xs={12} sm={2}>
                                <Form.Label className="small mb-1">Tipo de dato</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={draft.tipo}
                                    onChange={e => setDraft(p => ({ ...p, tipo: e.target.value }))}
                                >
                                    {TIPO_OPTIONS.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs="auto">
                                <Form.Check
                                    type="checkbox"
                                    label="Obligatorio"
                                    checked={draft.requerido}
                                    onChange={e => setDraft(p => ({ ...p, requerido: e.target.checked }))}
                                />
                            </Col>
                            <Col xs="auto">
                                <div className="d-flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={add}
                                        disabled={!draft.campo.trim() || !draft.label.trim()}
                                    >
                                        <FaIcons.FaCheck className="me-1" />Agregar
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => { setShowAdd(false); setDraft(EMPTY_CAMPO) }}
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {campos.length === 0 ? (
                <Alert variant="light" className="text-muted text-center py-3 small">
                    No hay variables definidas. Haz clic en "Agregar variable" para comenzar.
                </Alert>
            ) : (
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead className="table-light">
                        <tr>
                            <th>Variable</th>
                            <th>Descripción</th>
                            <th>Tipo</th>
                            <th style={{ width: 115 }}>Uso en el correo</th>
                            <th>Valor ejemplo</th>
                            <th style={{ width: 95 }} className="text-center">¿Obligatorio?</th>
                            <th style={{ width: 80 }} className="text-center">Estado</th>
                            <th style={{ width: 80 }} className="text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {campos.map((c, idx) => (
                            <tr key={idx} style={{ opacity: c.activo ? 1 : 0.5 }}>
                                <td><code style={{ color: '#7b1fa2' }}>{c.campo}</code></td>
                                <td>{c.label}</td>
                                <td>
                                    <Badge bg="light" text="dark" style={{ fontSize: '0.72rem', border: '1px solid #dee2e6' }}>
                                        {TIPO_LABELS[c.tipo] || c.tipo}
                                    </Badge>
                                </td>
                                <td>
                                    <code className="text-success" style={{ fontSize: '0.82rem' }}>{`{${c.campo}}`}</code>
                                </td>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={c.ejemplo || ''}
                                        onChange={e => onChange(campos.map((x, i) => i === idx ? { ...x, ejemplo: e.target.value } : x))}
                                        placeholder="valor de prueba"
                                        style={{ fontSize: '0.82rem', minWidth: 120 }}
                                    />
                                </td>
                                <td className="text-center">
                                    {c.requerido
                                        ? <Badge bg="danger" style={{ fontSize: '0.7rem' }}>Sí</Badge>
                                        : <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>No</Badge>
                                    }
                                </td>
                                <td className="text-center">
                                    <Badge bg={c.activo ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                                        {c.activo ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                </td>
                                <td className="text-center">
                                    <div className="d-flex gap-2 justify-content-center align-items-center" style={{ flexWrap: 'nowrap' }}>
                                        {c.activo
                                            ? <FaIcons.FaToggleOn
                                                title="Desactivar"
                                                style={{ color: '#ffc107', cursor: 'pointer', fontSize: '1.2em' }}
                                                onClick={() => toggle(idx, 'activo')}
                                              />
                                            : <FaIcons.FaToggleOff
                                                title="Activar"
                                                style={{ color: '#28a745', cursor: 'pointer', fontSize: '1.2em' }}
                                                onClick={() => toggle(idx, 'activo')}
                                              />
                                        }
                                        <FaIcons.FaTrash
                                            title="Eliminar"
                                            style={{ color: '#dc3545', cursor: 'pointer', fontSize: '1em' }}
                                            onClick={() => remove(idx)}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    )
}

// ─── CamposAutoTable ────────────────────────────────────────────────────────
const EMPTY_AUTO = { campo: '', descripcion: '', ejemplo: '' }

const CamposAutoTable = ({ campos, onChange }) => {
    const [showAdd, setShowAdd] = useState(false)
    const [draft, setDraft] = useState(EMPTY_AUTO)

    const remove = (idx) => onChange(campos.filter((_, i) => i !== idx))

    const add = () => {
        if (!draft.campo.trim()) return
        const sanitized = draft.campo.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_')
        onChange([...campos, { campo: sanitized, descripcion: draft.descripcion, ejemplo: draft.ejemplo }])
        setDraft(EMPTY_AUTO)
        setShowAdd(false)
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <strong>Variables automáticas del sistema</strong>
                    <div>
                        <small className="text-muted">
                            Se llenan automáticamente con datos del cierre al enviar. El <em>valor ejemplo</em> se usa en el correo de prueba.
                        </small>
                    </div>
                </div>
                <Button variant="outline-primary" size="sm" onClick={() => setShowAdd(v => !v)}>
                    <FaIcons.FaPlus className="me-1" />Agregar
                </Button>
            </div>

            {showAdd && (
                <Card className="mb-3 border-primary">
                    <Card.Body className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col xs={12} sm={3}>
                                <Form.Label className="small mb-1">Nombre variable *</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={draft.campo}
                                    onChange={e => setDraft(p => ({
                                        ...p,
                                        campo: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                                    }))}
                                    placeholder="ej: empresa_nombre"
                                />
                            </Col>
                            <Col xs={12} sm={4}>
                                <Form.Label className="small mb-1">Descripción</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={draft.descripcion}
                                    onChange={e => setDraft(p => ({ ...p, descripcion: e.target.value }))}
                                    placeholder="ej: Nombre de la empresa"
                                />
                            </Col>
                            <Col xs={12} sm={3}>
                                <Form.Label className="small mb-1">Valor ejemplo</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={draft.ejemplo}
                                    onChange={e => setDraft(p => ({ ...p, ejemplo: e.target.value }))}
                                    placeholder="ej: 01 de Abril"
                                />
                            </Col>
                            <Col xs="auto">
                                <div className="d-flex gap-2">
                                    <Button size="sm" variant="primary" onClick={add} disabled={!draft.campo.trim()}>
                                        <FaIcons.FaCheck className="me-1" />Agregar
                                    </Button>
                                    <Button size="sm" variant="outline-secondary" onClick={() => { setShowAdd(false); setDraft(EMPTY_AUTO) }}>
                                        Cancelar
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {campos.length === 0 ? (
                <Alert variant="light" className="text-muted text-center py-3 small">
                    No hay variables automáticas definidas.
                </Alert>
            ) : (
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead className="table-light">
                        <tr>
                            <th>Variable</th>
                            <th>Descripción</th>
                            <th style={{ width: 120 }}>Uso en el correo</th>
                            <th>Valor ejemplo</th>
                            <th style={{ width: 60 }} className="text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {campos.map((c, idx) => (
                            <tr key={idx}>
                                <td><code style={{ color: '#1976d2' }}>{c.campo}</code></td>
                                <td>{c.descripcion || <span className="text-muted">—</span>}</td>
                                <td><code className="text-success" style={{ fontSize: '0.82rem' }}>{`{${c.campo}}`}</code></td>
                                <td>
                                    <Form.Control
                                        size="sm"
                                        value={c.ejemplo || ''}
                                        onChange={e => onChange(campos.map((x, i) => i === idx ? { ...x, ejemplo: e.target.value } : x))}
                                        placeholder="valor de prueba"
                                        style={{ fontSize: '0.82rem', minWidth: 120 }}
                                    />
                                </td>
                                <td className="text-center">
                                    <FaIcons.FaTrash
                                        title="Eliminar"
                                        style={{ color: '#dc3545', cursor: 'pointer', fontSize: '1em' }}
                                        onClick={() => remove(idx)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    )
}

// ─── Main component ────────────────────────────────────────────────────────
const EmailTemplateEditor = () => {
    const history = useHistory()
    const { id } = useParams()
    const isNew = !id || id === 'nuevo'
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()

    // ── Metadata ──────────────────────────────────────────────────────────
    const [nombre, setNombre] = useState('')
    const [slug, setSlug] = useState('')
    const [descripcion, setDescripcion] = useState('')
    const [asunto, setAsunto] = useState('')
    const [activo, setActivo] = useState(true)
    const [camposConfig, setCamposConfig] = useState(DEFAULT_CAMPOS_CONFIG)
    const [camposAuto, setCamposAuto] = useState(DEFAULT_CAMPOS_AUTO)

    // ── Content ───────────────────────────────────────────────────────────
    const [quillContent, setQuillContent] = useState(DEFAULT_QUILL_CONTENT)

    // ── UI ────────────────────────────────────────────────────────────────
    const [loadingData, setLoadingData] = useState(false)
    const [savingMeta, setSavingMeta] = useState(false)
    const [savingHtml, setSavingHtml] = useState(false)
    const [errors, setErrors] = useState({})
    const [activeTab, setActiveTab] = useState('metadata')
    const [showPreview, setShowPreview] = useState(false)
    const [showTestModal, setShowTestModal] = useState(false)
    const [testEmail, setTestEmail] = useState('')
    const [sendingTest, setSendingTest] = useState(false)
    const iframeRef = useRef(null)

    // ── Load ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isNew) loadTemplate()
    }, [id])

    const loadTemplate = async () => {
        setLoadingData(true)
        try {
            const res = await getEmailTemplateContent(user, id)
            if (res.success && res.data) {
                const t = res.data
                setNombre(t.nombre || '')
                setSlug(t.slug || '')
                setDescripcion(t.descripcion || '')
                setAsunto(t.asunto || '')
                setActivo(t.activo !== false)
                const valoresEjemplo = t.valores_ejemplo
                    ? (typeof t.valores_ejemplo === 'string' ? JSON.parse(t.valores_ejemplo) : t.valores_ejemplo)
                    : {}
                setCamposConfig(parseCamposConfig(t.campos_config, valoresEjemplo))
                setCamposAuto(parseCamposAuto(t.campos_auto_cierre, valoresEjemplo))
                setQuillContent(
                    t.html_content
                        ? (extractBodyContent(t.html_content) || '')
                        : ''
                )
            } else {
                ErrorSwal('Error', res.error || 'No se pudo cargar el template')
                history.push('/mantenedores/cierre/email-templates')
            }
        } catch (e) {
            ErrorSwal('Error', 'Error inesperado al cargar el template')
        } finally {
            setLoadingData(false)
        }
    }

    // ── Validate ──────────────────────────────────────────────────────────
    const validateMeta = () => {
        const newErrors = {}
        if (!nombre.trim()) newErrors.nombre = 'El nombre es obligatorio'
        if (!slug.trim()) newErrors.slug = 'El slug es obligatorio'
        else if (!/^[a-z0-9_]+$/.test(slug.trim())) newErrors.slug = 'Solo minúsculas, números y guión bajo'
        if (!asunto.trim()) newErrors.asunto = 'El asunto es obligatorio'
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    // ── Save metadata ─────────────────────────────────────────────────────
    const handleSaveMeta = async () => {
        if (!validateMeta()) return
        setSavingMeta(true)
        try {
            // Construir valores_ejemplo desde ejemplo de cada campo
            const valoresEjemplo = {}
            camposConfig.forEach(c => { if (c.campo && c.ejemplo) valoresEjemplo[c.campo] = c.ejemplo })
            camposAuto.forEach(c => { if (c.campo && c.ejemplo) valoresEjemplo[c.campo] = c.ejemplo })

            const data = {
                nombre: nombre.trim(),
                slug: slug.trim(),
                descripcion: descripcion.trim(),
                asunto: asunto.trim(),
                activo,
                campos_config: JSON.stringify(camposConfig),
                campos_auto_cierre: JSON.stringify(camposAuto),
                valores_ejemplo: JSON.stringify(valoresEjemplo)
            }
            let res
            if (isNew) {
                res = await createEmailTemplate(user, data)
                if (res.success) {
                    SuccessSwal('Creado', 'Template creado correctamente')
                    history.replace(`/mantenedores/cierre/email-templates/${res.data.id}`)
                } else {
                    ErrorSwal('Error', res.error || 'No se pudo crear el template')
                }
            } else {
                res = await updateEmailTemplate(user, id, data)
                if (res.success) {
                    SuccessSwal('Guardado', 'Configuración actualizada correctamente')
                } else {
                    ErrorSwal('Error', res.error || 'No se pudo actualizar el template')
                }
            }
        } catch (e) {
            ErrorSwal('Error', 'Error inesperado al guardar')
        } finally {
            setSavingMeta(false)
        }
    }

    // ── Save HTML ─────────────────────────────────────────────────────────
    const handleSaveHtml = async () => {
        if (isNew) {
            ErrorSwal('Aviso', 'Debes guardar la configuración primero antes de guardar el contenido.')
            return
        }
        setSavingHtml(true)
        try {
            const res = await updateEmailTemplateContent(user, id, quillContent)
            if (res.success) {
                SuccessSwal('Guardado', 'Contenido del correo guardado correctamente')
            } else {
                ErrorSwal('Error', res.error || 'No se pudo guardar el contenido')
            }
        } catch (e) {
            ErrorSwal('Error', 'Error inesperado al guardar el contenido')
        } finally {
            setSavingHtml(false)
        }
    }

    // ── Preview iframe update ─────────────────────────────────────────────
    useEffect(() => {
        if (showPreview && iframeRef.current) {
            iframeRef.current.srcdoc = wrapBodyInHtml(quillContent)
        }
    }, [showPreview])

    // ── Test email ────────────────────────────────────────────────────────
    const handleSendTest = async () => {
        if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
            ErrorSwal('Error', 'Ingresa un correo válido')
            return
        }
        setSendingTest(true)
        try {
            const res = await testEmailTemplate(user, id, testEmail)
            if (res.success) {
                SuccessSwal('Enviado', `Correo de prueba enviado a ${testEmail}`)
                setShowTestModal(false)
                setTestEmail('')
            } else {
                ErrorSwal('Error', res.error || 'No se pudo enviar el correo de prueba')
            }
        } catch (e) {
            ErrorSwal('Error', 'Error inesperado al enviar el correo de prueba')
        } finally {
            setSendingTest(false)
        }
    }

    const breadcrumbs = [
        { label: 'Cierre', path: '/mantenedores/cierre' },
        { label: 'Templates', path: '/mantenedores/cierre/email-templates' },
        { label: isNew ? 'Nuevo' : nombre || 'Editar' }
    ]

    if (loadingData) {
        return (
            <SimpleMantenedorLayout
                mantenedorId="cierre"
                title="Editor de Template"
                subtitle=""
                iconColor="#7b1fa2"
                breadcrumbs={breadcrumbs}
                showBackButton={false}
            >
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2 text-muted">Cargando template...</div>
                </div>
            </SimpleMantenedorLayout>
        )
    }

    const allVariables = [
        ...camposConfig.filter(c => c.activo),
        ...camposAuto
    ]

    return (
        <SimpleMantenedorLayout
            mantenedorId="cierre"
            title={isNew ? 'Nuevo Template de Correo' : `Editar: ${nombre || 'Template'}`}
            subtitle="Configura el asunto, las variables y el contenido del correo"
            iconColor="#7b1fa2"
            breadcrumbs={breadcrumbs}
            showBackButton={false}
        >
            {/* Quill height override */}
            <style>{`
                .quill-editor-main .ql-editor { min-height: 420px; font-size: 14px; line-height: 1.7; }
                .quill-editor-main .ql-container { font-family: Arial, sans-serif; }
            `}</style>

            {/* Top action bar */}
            <Row className="mb-3">
                <Col xs={12} className="d-flex justify-content-between align-items-center">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => history.push('/mantenedores/cierre/email-templates')}
                    >
                        <FaIcons.FaArrowLeft className="me-1" /> Volver a la lista
                    </Button>
                    {!isNew && (
                        <Button variant="outline-info" size="sm" onClick={() => setShowTestModal(true)}>
                            <FaIcons.FaEnvelope className="me-1" /> Enviar prueba
                        </Button>
                    )}
                </Col>
            </Row>

            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" className="mb-0">
                    <Nav.Item>
                        <Nav.Link eventKey="metadata">
                            <FaIcons.FaCog className="me-1" /> Configuración
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="editor" disabled={isNew}>
                            <FaIcons.FaEdit className="me-1" /> Editor de Contenido
                            {isNew && <small className="text-muted ms-1">(guarda primero)</small>}
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    {/* ── TAB: Configuración ──────────────────────────── */}
                    <Tab.Pane eventKey="metadata">
                        <Card className="border-top-0 rounded-0 rounded-bottom shadow-sm">
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nombre *</Form.Label>
                                            <Form.Control
                                                value={nombre}
                                                onChange={e => setNombre(e.target.value)}
                                                placeholder="Ej: Cierre Definitivo"
                                                isInvalid={!!errors.nombre}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.nombre}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Slug *
                                                <small className="text-muted ms-2">(identificador único, solo a-z 0-9 _)</small>
                                            </Form.Label>
                                            <Form.Control
                                                value={slug}
                                                onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                                placeholder="ej: cierre_definitivo"
                                                isInvalid={!!errors.slug}
                                                disabled={!isNew}
                                            />
                                            {!isNew && (
                                                <Form.Text className="text-muted">El slug no puede cambiarse una vez creado</Form.Text>
                                            )}
                                            <Form.Control.Feedback type="invalid">{errors.slug}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Asunto del correo *</Form.Label>
                                    <Form.Control
                                        value={asunto}
                                        onChange={e => setAsunto(e.target.value)}
                                        placeholder="Ej: Cierre {cierre} - Periodo {periodo}"
                                        isInvalid={!!errors.asunto}
                                    />
                                    <Form.Text className="text-muted">
                                        Puedes insertar variables usando {'{nombre_variable}'}
                                    </Form.Text>
                                    <Form.Control.Feedback type="invalid">{errors.asunto}</Form.Control.Feedback>
                                </Form.Group>

                                <Row>
                                    <Col md={9}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Descripción interna</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={descripcion}
                                                onChange={e => setDescripcion(e.target.value)}
                                                placeholder="Descripción para identificar este template"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} className="d-flex align-items-center">
                                        <Form.Group className="mb-3 w-100">
                                            <Form.Label className="d-block">&nbsp;</Form.Label>
                                            <Form.Check
                                                type="checkbox"
                                                id="activo"
                                                label="Template activo"
                                                checked={activo}
                                                onChange={e => setActivo(e.target.checked)}
                                            />
                                            <Form.Text className="text-muted">
                                                Aparece en el modal de envío
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr />

                                <div className="mb-4">
                                    <CamposConfigTable campos={camposConfig} onChange={setCamposConfig} />
                                </div>

                                <hr />

                                <div className="mb-3">
                                    <CamposAutoTable campos={camposAuto} onChange={setCamposAuto} />
                                </div>

                                <div className="d-flex justify-content-end mt-3">
                                    <Button variant="primary" onClick={handleSaveMeta} disabled={savingMeta}>
                                        {savingMeta
                                            ? <><Spinner size="sm" className="me-2" />Guardando...</>
                                            : <><FaIcons.FaSave className="me-2" />{isNew ? 'Crear Template' : 'Guardar Configuración'}</>
                                        }
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* ── TAB: Editor de Contenido ─────────────────────── */}
                    <Tab.Pane eventKey="editor">
                        <Card className="border-top-0 rounded-0 rounded-bottom shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <small className="text-muted">
                                        <FaIcons.FaEdit className="me-1" />
                                        Escribe el contenido del correo. Usa <code className="text-success">{'{variable}'}</code> para insertar datos dinámicos.
                                    </small>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setShowPreview(true)}
                                        >
                                            <FaIcons.FaEye className="me-1" />Vista previa
                                        </Button>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleSaveHtml}
                                            disabled={savingHtml}
                                        >
                                            {savingHtml
                                                ? <><Spinner size="sm" className="me-2" />Guardando...</>
                                                : <><FaIcons.FaSave className="me-1" />Guardar contenido</>
                                            }
                                        </Button>
                                    </div>
                                </div>

                                <ReactQuill
                                    className="quill-editor-main"
                                    theme="snow"
                                    value={quillContent}
                                    onChange={setQuillContent}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                />

                                {allVariables.length > 0 && (() => {
                                    const usadas = allVariables.filter(c => quillContent.includes(`{${c.campo}}`))
                                    const noUsadas = allVariables.filter(c => !quillContent.includes(`{${c.campo}}`))
                                    return (
                                        <Card className="mt-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                                            <Card.Body className="py-2 px-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <small className="fw-bold text-muted">
                                                        <FaIcons.FaInfoCircle className="me-1 text-info" />
                                                        Variables disponibles — cópialas y pégalas en el texto
                                                    </small>
                                                    <small>
                                                        {noUsadas.length === 0
                                                            ? (
                                                                <span className="text-success fw-semibold">
                                                                    <FaIcons.FaCheckCircle className="me-1" />
                                                                    Todas las variables están en uso
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#e65100' }}>
                                                                    <FaIcons.FaExclamationTriangle className="me-1" />
                                                                    {usadas.length} usada{usadas.length !== 1 ? 's' : ''},{' '}
                                                                    <strong>{noUsadas.length} sin usar</strong>
                                                                </span>
                                                            )
                                                        }
                                                    </small>
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {allVariables.map((c, i) => {
                                                        const usada = quillContent.includes(`{${c.campo}}`)
                                                        return (
                                                            <span
                                                                key={i}
                                                                title={usada
                                                                    ? `${c.label || c.descripcion || c.campo} — ✓ utilizada en el texto`
                                                                    : `${c.label || c.descripcion || c.campo} — no aparece en el texto`
                                                                }
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                                            >
                                                                {usada
                                                                    ? <FaIcons.FaCheckCircle style={{ color: '#28a745', fontSize: '0.72em', flexShrink: 0 }} />
                                                                    : <FaIcons.FaExclamationCircle style={{ color: '#ffc107', fontSize: '0.72em', flexShrink: 0 }} />
                                                                }
                                                                <code style={{
                                                                    backgroundColor: usada ? '#f0fff4' : '#fff8e1',
                                                                    color: usada ? '#28a745' : '#e65100',
                                                                    padding: '1px 6px',
                                                                    borderRadius: 3,
                                                                    border: `1px solid ${usada ? '#c3e6cb' : '#ffe082'}`,
                                                                    fontSize: '0.82em'
                                                                }}>
                                                                    {`{${c.campo}}`}
                                                                </code>
                                                            </span>
                                                        )
                                                    })}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    )
                                })()}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* ── Preview Modal ──────────────────────────────────────────── */}
            <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaIcons.FaEye className="me-2" />Vista previa del correo
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ height: '70vh' }}>
                    <iframe
                        ref={iframeRef}
                        title="email-preview"
                        sandbox="allow-same-origin"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <small className="text-muted me-auto">
                        Las variables como <code>{'{campo}'}</code> aparecen tal cual; se reemplazan con datos reales al enviar
                    </small>
                    <Button variant="secondary" onClick={() => setShowPreview(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Test Email Modal ───────────────────────────────────────── */}
            <Modal show={showTestModal} onHide={() => setShowTestModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaIcons.FaEnvelope className="me-2 text-info" />Enviar correo de prueba
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="small">
                        Se enviará el template con valores de ejemplo para que puedas revisar el formato antes de enviar masivamente.
                    </Alert>
                    <Form.Group>
                        <Form.Label>Email de destino</Form.Label>
                        <Form.Control
                            type="email"
                            value={testEmail}
                            onChange={e => setTestEmail(e.target.value)}
                            placeholder="correo@ejemplo.com"
                            onKeyDown={e => e.key === 'Enter' && handleSendTest()}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTestModal(false)} disabled={sendingTest}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSendTest} disabled={sendingTest || !testEmail}>
                        {sendingTest
                            ? <><Spinner size="sm" className="me-2" />Enviando...</>
                            : <><FaIcons.FaEnvelope className="me-1" />Enviar prueba</>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </SimpleMantenedorLayout>
    )
}

export default EmailTemplateEditor
